/* iter-50b — Stacks Page (Phase B).
 *
 * One unified search surface for the author's drafts AND the bookstore
 * catalogue. Five filter axes per spec:
 *   - free-text (title | author)
 *   - author
 *   - genre
 *   - ISBN
 *   - publication year range
 *
 * Visibility (iter-50f — tightened):
 *   - Writer (regular auth) → own projects + own published books only.
 *   - Admin  (admin cookie) → all projects + all books.
 *   - The route itself is wrapped in <RequireAuth> in App.js so an
 *     unauthenticated visitor is bounced to /login.
 *
 * Each result card is clickable; opens the Phase C
 * "promote-title" multi-step dialog.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search, X, Filter, BookOpen, PenLine, Loader2,
  ArrowLeft, ShieldAlert,
} from 'lucide-react';
import StacksTitleDialog from '@/components/dialogs/StacksTitleDialog';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SOURCE_OPTS_AUTHOR = [
  { v: 'all',     label: 'Everything'           },
  { v: 'project', label: 'My drafts'            },
  { v: 'book',    label: 'My published books'   },
];
const SOURCE_OPTS_ADMIN = [
  { v: 'all',     label: 'Everything'   },
  { v: 'project', label: 'All drafts'   },
  { v: 'book',    label: 'All books'    },
];

export default function StacksPage() {
  const nav = useNavigate();
  const [q,         setQ]         = useState('');
  const [author,    setAuthor]    = useState('');
  const [genre,     setGenre]     = useState('');
  const [isbn,      setIsbn]      = useState('');
  const [yearFrom,  setYearFrom]  = useState('');
  const [yearTo,    setYearTo]    = useState('');
  const [source,    setSource]    = useState('all');
  const [showAdv,   setShowAdv]   = useState(false);
  const [rows,      setRows]      = useState(null);
  const [isAdmin,   setIsAdmin]   = useState(false);
  const [busy,      setBusy]      = useState(false);
  // iter-50d — Phase C router dialog
  const [selected,  setSelected]  = useState(null);

  // Debounced fetch on filter change.
  const debounce = useRef(null);
  // iter-50l — Cancel any in-flight request when the user types again so a
  // slow earlier response can never overwrite a faster later one.
  const abortRef = useRef(null);
  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      runSearch();
    }, 250);
    return () => {
      if (debounce.current) clearTimeout(debounce.current);
      if (abortRef.current) abortRef.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, author, genre, isbn, yearFrom, yearTo, source]);

  const runSearch = async () => {
    // Cancel any prior in-flight request before starting a new one.
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setBusy(true);
    try {
      const params = {};
      if (q)        params.q         = q;
      if (author)   params.author    = author;
      if (genre)    params.genre     = genre;
      if (isbn)     params.isbn      = isbn;
      if (yearFrom) params.year_from = Number(yearFrom);
      if (yearTo)   params.year_to   = Number(yearTo);
      params.source = source;
      params.limit  = 60;
      const { data } = await axios.get(`${API}/stacks/search`, {
        params, withCredentials: true, timeout: 20000, signal: ctrl.signal,
      });
      // Only commit if we are still the latest in-flight request.
      if (abortRef.current !== ctrl) return;
      setRows(data.items || []);
      setIsAdmin(!!data.is_admin);
    } catch (err) {
      // axios surfaces aborts as `CanceledError`; swallow them — the new
      // request will set state shortly.
      if (axios.isCancel?.(err) || err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
      if (abortRef.current !== ctrl) return;
      toast.error(err?.response?.data?.detail || err.message || 'Search failed');
      setRows([]);
    } finally {
      if (abortRef.current === ctrl) setBusy(false);
    }
  };

  const reset = () => {
    setQ(''); setAuthor(''); setGenre(''); setIsbn('');
    setYearFrom(''); setYearTo(''); setSource('all');
  };

  const hasFilters = useMemo(
    () => Boolean(q || author || genre || isbn || yearFrom || yearTo || source !== 'all'),
    [q, author, genre, isbn, yearFrom, yearTo, source],
  );

  return (
    <div className="h-full overflow-y-auto bg-background text-foreground scroll-thin" data-testid="stacks-page">
      {/* Top bar */}
      <div className="sticky top-0 z-20 border-b border-border bg-background/95 px-4 py-3 backdrop-blur sm:px-6">
        <div className="mx-auto flex max-w-6xl items-center gap-3">
          <button
            type="button"
            onClick={() => nav('/app')}
            data-testid="stacks-back-btn"
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[12px] text-muted-foreground hover:bg-secondary hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> The Loom
          </button>
          <h1 className="text-[15px] font-semibold tracking-tight">The Stacks</h1>
          {isAdmin && (
            <span data-testid="stacks-admin-badge" className="inline-flex items-center gap-1 rounded-sm border border-amber-300/70 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-800">
              <ShieldAlert className="h-3 w-3" /> Admin view
            </span>
          )}
          <div className="ml-auto text-[11.5px] text-muted-foreground">
            {busy ? <Loader2 className="inline h-3 w-3 animate-spin" /> : rows ? `${rows.length} result${rows.length === 1 ? '' : 's'}` : ''}
          </div>
        </div>
      </div>

      {/* Console */}
      <div className="border-b border-border bg-card/40 px-4 py-4 sm:px-6">
        <div className="mx-auto max-w-6xl space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[260px]">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by title or author…"
                data-testid="stacks-search-input"
                className="h-10 w-full rounded-md border border-border bg-background pl-9 pr-9 text-[13.5px] outline-none focus:border-primary"
                autoFocus
              />
              {q && (
                <button
                  type="button"
                  onClick={() => setQ('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
                  aria-label="Clear search"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <select
              value={source}
              onChange={(e) => setSource(e.target.value)}
              data-testid="stacks-source-select"
              className="h-10 rounded-md border border-border bg-background px-2 text-[12.5px] outline-none focus:border-primary"
            >
              {(isAdmin ? SOURCE_OPTS_ADMIN : SOURCE_OPTS_AUTHOR).map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
            <button
              type="button"
              onClick={() => setShowAdv((v) => !v)}
              data-testid="stacks-advanced-toggle"
              className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-[12.5px] ${showAdv ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-background'} hover:border-primary/60`}
            >
              <Filter className="h-3.5 w-3.5" /> Filters
            </button>
            {hasFilters && (
              <button
                type="button"
                onClick={reset}
                data-testid="stacks-reset-btn"
                className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-[12px] text-muted-foreground hover:bg-secondary hover:text-foreground"
              >
                Reset
              </button>
            )}
          </div>

          {showAdv && (
            <div data-testid="stacks-advanced-panel" className="grid grid-cols-2 gap-2 sm:grid-cols-5">
              <input value={author}   onChange={(e) => setAuthor(e.target.value)}   placeholder="Author"
                     data-testid="stacks-author-input"
                     className="h-9 rounded-md border border-border bg-background px-2 text-[12.5px] outline-none focus:border-primary" />
              <input value={genre}    onChange={(e) => setGenre(e.target.value)}    placeholder="Genre slug"
                     data-testid="stacks-genre-input"
                     className="h-9 rounded-md border border-border bg-background px-2 text-[12.5px] outline-none focus:border-primary" />
              <input value={isbn}     onChange={(e) => setIsbn(e.target.value)}     placeholder="ISBN"
                     data-testid="stacks-isbn-input"
                     className="h-9 rounded-md border border-border bg-background px-2 text-[12.5px] outline-none focus:border-primary" />
              <input value={yearFrom} onChange={(e) => setYearFrom(e.target.value)} placeholder="From year"
                     type="number" min="1450" max="2100"
                     data-testid="stacks-yearfrom-input"
                     className="h-9 rounded-md border border-border bg-background px-2 text-[12.5px] outline-none focus:border-primary" />
              <input value={yearTo}   onChange={(e) => setYearTo(e.target.value)}   placeholder="To year"
                     type="number" min="1450" max="2100"
                     data-testid="stacks-yearto-input"
                     className="h-9 rounded-md border border-border bg-background px-2 text-[12.5px] outline-none focus:border-primary" />
            </div>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
        {rows === null ? (
          <div className="flex h-64 items-center justify-center text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-md border border-dashed border-border bg-card p-10 text-center text-[13px] text-muted-foreground" data-testid="stacks-empty">
            {(() => {
              // 2026-10 — Bug fix: this empty state used to show the same
              // "No titles match these filters" copy even with zero active
              // filters — e.g. a brand-new author picking "My published
              // books" (nothing published yet) saw wording that implied
              // something was broken instead of a reassuring placeholder.
              const hasActiveFilters = Boolean(q || author || genre || isbn || yearFrom || yearTo);
              if (hasActiveFilters) {
                return 'No titles match these filters. Try clearing the year range or switching scope.';
              }
              if (source === 'book') {
                return isAdmin
                  ? 'No published books yet across the platform.'
                  : 'Your finished works will be here. Publish a manuscript from The Loom to see it in The Stacks.';
              }
              if (source === 'project') {
                return isAdmin
                  ? 'No drafts yet across the platform.'
                  : 'Your drafts will be here. Start a new project in The Loom to see it in The Stacks.';
              }
              return isAdmin
                ? 'Nothing in The Stacks yet.'
                : 'The Stacks is empty for now — start a draft or publish a book to see it here.';
            })()}
            {!isAdmin && (
              <div className="mt-5">
                <Button
                  onClick={() => nav('/')}
                  className="gap-2"
                  data-testid="stacks-open-loom-btn"
                >
                  <PenLine className="h-4 w-4" />
                  Open The Loom
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="stacks-results">
            {rows.map((r) => <ResultCard key={`${r.source}-${r.id}`} row={r} onSelect={setSelected} />)}
          </div>
        )}
      </div>

      <StacksTitleDialog
        open={!!selected}
        row={selected}
        isAdmin={isAdmin}
        onClose={() => setSelected(null)}
        onChanged={() => runSearch()}
      />
    </div>
  );
}

function ResultCard({ row, onSelect }) {
  const isProject = row.source === 'project';
  const Icon = isProject ? PenLine : BookOpen;
  const cover = row.cover_url;
  const onClick = () => onSelect?.(row);
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`stacks-card-${row.source}-${row.id}`}
      className="group flex gap-3 rounded-md border border-border bg-card p-3 text-left transition hover:border-primary/50 hover:shadow-panel-active"
    >
      <div className="relative h-24 w-16 shrink-0 overflow-hidden rounded-sm bg-secondary">
        {cover ? (
          <img src={cover} alt="" className="h-full w-full object-cover transition group-hover:scale-105" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-muted-foreground">
            <Icon className="h-5 w-5" />
          </div>
        )}
        <span
          className={`absolute left-1 top-1 rounded-sm px-1 py-0.5 text-[8.5px] font-semibold uppercase tracking-wider ${
            isProject
              ? 'bg-primary text-primary-foreground'
              : 'bg-emerald-600 text-white'
          }`}
        >
          {isProject ? 'Draft' : 'Bookstore'}
        </span>
      </div>
      <div className="flex flex-1 flex-col">
        <h3 className="text-[13.5px] font-semibold leading-tight line-clamp-2">{row.title}</h3>
        <p className="mt-0.5 text-[11.5px] text-muted-foreground">by {row.author || '—'}</p>
        <div className="mt-auto flex flex-wrap items-center gap-1 pt-2 text-[10.5px] text-muted-foreground">
          {row.genre && <span className="rounded-sm bg-secondary px-1.5 py-0.5">{row.genre}</span>}
          {row.publication_year && <span>{row.publication_year}</span>}
          {row.isbn && <span className="font-mono">{row.isbn}</span>}
          {row.word_count > 0 && (
            <span className="font-mono">{row.word_count.toLocaleString()} w</span>
          )}
        </div>
      </div>
    </button>
  );
}
